import {useApolloClient} from '@apollo/client';
import {useCallback, useEffect, useState} from 'react';
import {useCurrentLanguage} from '~Store/Slices/LanguageSlice/Language.selector';
import languageSlice from '~Store/Slices/LanguageSlice/Language.slice';
import {useAppDispatch} from '@src/Store/hooks';
import {useI18next} from 'gatsby-plugin-react-i18next';
import {languageMapper, languageUnmap} from '@src/Services/i18n/languageMapper';
import useInitSetting from '@src/Services/Query/hooks/useInitSetting';

const useChangeLanguage = () => {
  const client = useApolloClient();
  const {init} = useInitSetting();
  const currentLanguage = useCurrentLanguage();
  const {language: currentI18nLanguage, changeLanguage} = useI18next();
  const dispatch = useAppDispatch();
  const [isUpdating, setIsUpdating] = useState(false);

  useEffect(() => {
    const lang = languageUnmap(currentI18nLanguage);
    if (lang === currentLanguage) return;

    dispatch(languageSlice.actions.setCurrentLanguage(lang));
  }, [currentI18nLanguage]);

  useEffect(() => {
    const updateContent = async () => await client.reFetchObservableQueries();

    const lang = languageUnmap(currentI18nLanguage);
    if (isUpdating && lang === currentLanguage) {
      updateContent();
      setIsUpdating(false);
    }
  }, [currentI18nLanguage, currentLanguage, isUpdating]);

  const updateApi = useCallback(
    async (lang: string) => {
      try {
        // First stop any in-flight queries
        client.stop();
        // Then clear the store
        await client.clearStore();
        // Finally reset the store
        await client.resetStore();
        await client.reFetchObservableQueries();
      } catch (error) {
        console.error('Error resetting Apollo store:', error);
      }
    },
    [client],
  );

  const updateStoreLang = async (language: string) => {
    if (isUpdating) return;
    setIsUpdating(true);

    try {
      // Update Redux store first
      dispatch(languageSlice.actions.setCurrentLanguage(language));

      // Finally reset Apollo store
      await updateApi(language);

      await init();

      // Then update i18n
      changeLanguage(languageMapper(language), undefined, {
        replace: true,
      });
    } catch (error) {
      console.error('updateStoreLang error', error);
    } finally {
      setIsUpdating(false);
      console.log('[LangSwitch] Switch Language OK');
    }
  };

  /** New implementation to change language (still in test but will replace everything) */
  const changeCurrentLanguage = async (langIsoCode: string) => {
    if (isUpdating || currentI18nLanguage === langIsoCode) return;
    setIsUpdating(true);

    const lang = languageMapper(langIsoCode);

    changeLanguage(lang, undefined, {replace: true});
  };

  return {
    currentLanguage,
    updateStoreLang,
    changeCurrentLanguage,
  };
};

export default useChangeLanguage;
